import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, Mail, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { Layout } from '../components/ui/Layout';
import { 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  ErrorMessage, 
  Divider, 
  DividerText 
} from '../components/ui/Form';
import { theme } from '../styles/theme';
import { authService } from '../services/authService';
import { RegisterRequest } from '../types/auth';

const InputContainer = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textTertiary};
  z-index: 1;
`;

const StyledInput = styled(Input)`
  padding-left: ${theme.spacing['3xl']};
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.md};
  transition: color ${theme.transitions.fast}, background-color ${theme.transitions.fast}, transform ${theme.transitions.fast};
  
  &:hover {
    color: ${theme.colors.primaryLight};
    background-color: rgba(255, 255, 255, 0.08);
    transform: translateY(-50%) translateY(-1px);
  }
`;

const PasswordStrength = styled.div<{ $strength: number }>`
  height: 4px;
  background-color: ${theme.colors.gray200};
  border-radius: ${theme.borderRadius.full};
  margin-top: ${theme.spacing.sm};
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $strength }) => ($strength / 4) * 100}%;
    background-color: ${({ $strength }) => {
      if ($strength <= 1) return theme.colors.error;
      if ($strength <= 2) return theme.colors.warning;
      if ($strength <= 3) return theme.colors.info;
      return theme.colors.success;
    }};
    transition: all ${theme.transitions.normal};
  }
`;

const PasswordStrengthText = styled.div<{ $strength: number }>`
  font-size: ${theme.fontSizes.xs};
  margin-top: ${theme.spacing.xs};
  color: ${({ $strength }) => {
    if ($strength <= 1) return theme.colors.error;
    if ($strength <= 2) return theme.colors.warning;
    if ($strength <= 3) return theme.colors.info;
    return theme.colors.success;
  }};
`;

const TermsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  margin: ${theme.spacing.md} 0;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${theme.colors.primary};
  margin-top: 2px;
`;

const TermsText = styled.label`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  line-height: 1.4;
  cursor: pointer;
`;

const TermsLink = styled.a`
  color: ${theme.colors.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SignInPrompt = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
`;

const SignInLink = styled(Link)`
  color: ${theme.colors.primary};
  font-weight: ${theme.fontWeights.medium};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

interface RegisterFormData extends RegisterRequest {
  acceptTerms: boolean;
}

const validationSchema: yup.ObjectSchema<RegisterFormData> = yup.object({
  username: yup
    .string()
    .required('请输入账号')
    .min(3, '账号至少 3 个字符')
    .max(20, '账号不超过 20 个字符')
    .matches(/^[a-zA-Z0-9_]+$/, '账号仅支持字母、数字与下划线'),
  email: yup
    .string()
    .required('请输入邮箱')
    .email('请输入有效的邮箱地址'),
  password: yup
    .string()
    .required('请输入密码')
    .min(6, '密码至少 6 位')
    .matches(/(?=.*[a-z])/, '密码需包含至少 1 个小写字母')
    .matches(/(?=.*[A-Z])/, '密码需包含至少 1 个大写字母')
    .matches(/(?=.*\d)/, '密码需包含至少 1 个数字'),
  confirmPassword: yup
    .string()
    .required('请再次输入密码')
    .oneOf([yup.ref('password')], '两次输入的密码不一致'),
  acceptTerms: yup
    .boolean()
    .required()
    .oneOf([true], '请先阅读并同意服务条款'),
});

const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 6) strength++;
  if (/(?=.*[a-z])/.test(password)) strength++;
  if (/(?=.*[A-Z])/.test(password)) strength++;
  if (/(?=.*\d)/.test(password)) strength++;
  if (/(?=.*[!@#$%^&*])/.test(password)) strength++;
  return Math.min(strength, 4);
};

const getPasswordStrengthText = (strength: number): string => {
  if (strength <= 1) return '偏弱';
  if (strength <= 2) return '一般';
  if (strength <= 3) return '良好';
  return '较强';
};

export const Register: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm<RegisterFormData>({
    resolver: yupResolver(validationSchema),
  });

  const password = watch('password');

  React.useEffect(() => {
    if (password) {
      setPasswordStrength(calculatePasswordStrength(password));
    } else {
      setPasswordStrength(0);
    }
  }, [password]);

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    
    try {
      const response = await authService.register(data);
      
      if (response.success) {
        toast.success('注册成功，请登录继续。');
        navigate('/login');
      } else {
        if (response.error?.includes('username')) {
          setError('username', { message: response.error });
        } else if (response.error?.includes('email')) {
          setError('email', { message: response.error });
        } else {
          toast.error(response.error || '注册失败');
        }
      }
    } catch (error) {
      toast.error('系统繁忙，请稍后再试');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout
      title="申请账号"
      subtitle="填写信息并提交注册申请"
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="username">账号</Label>
          <InputContainer>
            <InputIcon>
              <User size={20} />
            </InputIcon>
            <StyledInput
              id="username"
              type="text"
              placeholder="请输入账号"
              $hasError={!!errors.username}
              {...register('username')}
            />
          </InputContainer>
          {errors.username && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.username.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">邮箱</Label>
          <InputContainer>
            <InputIcon>
              <Mail size={20} />
            </InputIcon>
            <StyledInput
              id="email"
              type="email"
              placeholder="请输入邮箱"
              $hasError={!!errors.email}
              {...register('email')}
            />
          </InputContainer>
          {errors.email && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.email.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">密码</Label>
          <InputContainer>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <StyledInput
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="请输入密码"
              $hasError={!!errors.password}
              {...register('password')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </InputContainer>
          {password && (
            <>
              <PasswordStrength $strength={passwordStrength} />
              <PasswordStrengthText $strength={passwordStrength}>
                密码强度：{getPasswordStrengthText(passwordStrength)}
              </PasswordStrengthText>
            </>
          )}
          {errors.password && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.password.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">确认密码</Label>
          <InputContainer>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <StyledInput
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="请再次输入密码"
              $hasError={!!errors.confirmPassword}
              {...register('confirmPassword')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </InputContainer>
          {errors.confirmPassword && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.confirmPassword.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <TermsContainer>
          <Checkbox 
            type="checkbox" 
            id="acceptTerms"
            {...register('acceptTerms')}
          />
          <TermsText htmlFor="acceptTerms">
            我已阅读并同意{' '}
            <TermsLink href="#" target="_blank" rel="noopener noreferrer">
              服务条款
            </TermsLink>{' '}
            与{' '}
            <TermsLink href="#" target="_blank" rel="noopener noreferrer">
              隐私政策
            </TermsLink>
          </TermsText>
        </TermsContainer>
        {errors.acceptTerms && (
          <ErrorMessage>
            <AlertCircle size={16} />
            {errors.acceptTerms.message}
          </ErrorMessage>
        )}

        <Button 
          type="submit" 
          $fullWidth 
          $loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? '正在提交...' : '提交注册'}
        </Button>

        <Divider>
          <DividerText>或</DividerText>
        </Divider>

        <SignInPrompt>
          已有账号？{' '}
          <SignInLink to="/login">返回登录</SignInLink>
        </SignInPrompt>
      </Form>
    </Layout>
  );
};
